import { ApiProblem, newIdempotencyKey, request } from "./transport";

export interface AgentRolePrompts {
  product_manager: string;
  approver: string;
  engineer: string;
  qa: string;
}

export interface CustomAgentRole {
  key: string;
  name: string;
  purpose: string;
  prompt: string;
  authority: "advisory";
}

export interface BoardAutomation {
  id: string;
  board_id: string;
  repository: string;
  repository_code_name: string | null;
  default_branch: string;
  github_login: string;
  github_account: string | null;
  credential_kind: "github_app" | "personal_access_token";
  credential_status: "connected";
  role_prompts: AgentRolePrompts;
  custom_roles: CustomAgentRole[];
  approval_mode: "agent" | "human";
  max_qa_rounds: number;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface BoardAutomationInput {
  repository: string;
  repository_code_name?: string | null;
  default_branch?: string | null;
  github_token?: string | null;
  role_prompts: AgentRolePrompts;
  custom_roles: CustomAgentRole[];
  approval_mode: "agent" | "human";
  max_qa_rounds: number;
}

export interface GitHubRepositoryOption {
  id: number;
  full_name: string;
  default_branch: string;
  private: boolean;
  owner: string;
}

export interface GitHubAppConnectionStart {
  id: string;
  status: "awaiting_authorization" | "ready";
  authorize_url: string | null;
  expires_at: string;
}

export interface GitHubAppConnection {
  id: string;
  status:
    | "awaiting_authorization"
    | "awaiting_installation"
    | "ready"
    | "consumed"
    | "failed"
    | "expired";
  app_name: string;
  github_user_login: string | null;
  installation_account: string | null;
  installation_target_type: string | null;
  manage_url: string | null;
  repositories: GitHubRepositoryOption[];
  failure_code: string | null;
  expires_at: string;
}

export interface GitHubAppConnectInput {
  repository_id: number;
  repository_code_name?: string | null;
  role_prompts: AgentRolePrompts;
  custom_roles: CustomAgentRole[];
  approval_mode: "agent" | "human";
  max_qa_rounds: number;
}

export interface OnboardingPrerequisite {
  key: "chatgpt" | "repository" | "roles" | "authority";
  label: string;
  status: "complete" | "action_required";
  detail: string;
}

export interface BoardAutomationOnboarding {
  id: string;
  board_id: string;
  status: "in_progress" | "completed";
  current_step: string;
  role_prompts: AgentRolePrompts;
  custom_roles: CustomAgentRole[];
  approval_mode: "agent" | "human";
  max_qa_rounds: number;
  prerequisites: OnboardingPrerequisite[];
  ready_to_complete: boolean;
  version: number;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BoardAutomationOnboardingPatch {
  current_step?: "prerequisites" | "repository" | "roles" | "guardrails" | "review";
  role_prompts?: AgentRolePrompts;
  custom_roles?: CustomAgentRole[];
  approval_mode?: "agent" | "human";
  max_qa_rounds?: number;
}

export interface WorkflowRoles {
  product_manager: boolean;
  engineer: boolean;
  qa: boolean;
}

export interface WorkflowYeetInput {
  roles: WorkflowRoles;
  approver?: "agent" | "human";
}

export interface TicketWorkflow {
  id: string;
  board_id: string;
  ticket_id: string;
  status: string;
  repository: string;
  default_branch: string;
  branch_name: string | null;
  approval_mode: "agent" | "human";
  enabled_roles: WorkflowRoles;
  max_qa_rounds: number;
  qa_round: number;
  spec_revision_round: number;
  preview_repair_round: number;
  max_preview_repair_rounds: number;
  current_run_id: string | null;
  spec_markdown: string | null;
  last_feedback: string | null;
  pr_number: number | null;
  pr_url: string | null;
  head_sha: string | null;
  preview_url: string | null;
  preview_console_url: string | null;
  preview_failure_summary: string | null;
  failure_code: string | null;
  result: Record<string, unknown> | null;
  started_at: string;
  finished_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface TicketWorkflowPage {
  items: TicketWorkflow[];
  total: number;
}

export interface AgentFailure {
  code: string;
  classification: string;
  title: string;
  detail: string;
  recovery: string;
  retryable: boolean;
}

export interface AgentRunActivity {
  id: string;
  phase: string;
  revision_reason: "qa_feedback" | "preview_deploy_failure" | null;
  status: string;
  attempt: number;
  requested_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  finished_at: string | null;
  deadline_at: string;
  exit_classification: string | null;
  error_code: string | null;
  summary: string | null;
  details: Record<string, unknown> | null;
  failure: AgentFailure | null;
}

export interface AgentProgressEvent {
  id: string;
  run_id: string;
  sequence: number;
  phase: string;
  source: string;
  kind: string;
  level: string;
  message: string;
  detail: Record<string, unknown> | null;
  occurred_at: string;
}

export interface TicketWorkflowActivity {
  workflow_id: string;
  status: string;
  active: boolean;
  generated_at: string;
  runs: AgentRunActivity[];
  events: AgentProgressEvent[];
  total_events: number;
  events_truncated: boolean;
  failure: AgentFailure | null;
}

export interface WorkflowActivityQuery {
  runId?: string;
  afterSequence?: number;
  limit?: number;
}

export async function getBoard(boardId: string): Promise<BoardAutomation | null> {
  try {
    return (
      await request<BoardAutomation>("GET", `/api/v1/boards/${boardId}/automation`)
    ).body;
  } catch (error) {
    if (error instanceof ApiProblem && error.status === 404) return null;
    throw error;
  }
}

export async function startOnboarding(
  boardId: string,
): Promise<BoardAutomationOnboarding> {
  return (
    await request<BoardAutomationOnboarding>(
      "POST",
      `/api/v1/boards/${boardId}/automation/onboarding`,
      {},
      { idempotencyKey: newIdempotencyKey() },
    )
  ).body;
}

export async function patchOnboarding(
  onboarding: BoardAutomationOnboarding,
  input: BoardAutomationOnboardingPatch,
): Promise<BoardAutomationOnboarding> {
  return (
    await request<BoardAutomationOnboarding>(
      "PATCH",
      `/api/v1/boards/${onboarding.board_id}/automation/onboarding/${onboarding.id}`,
      input,
      { etag: onboarding.version },
    )
  ).body;
}

export async function completeOnboarding(
  onboarding: BoardAutomationOnboarding,
): Promise<BoardAutomationOnboarding> {
  return (
    await request<BoardAutomationOnboarding>(
      "POST",
      `/api/v1/boards/${onboarding.board_id}/automation/onboarding/${onboarding.id}/complete`,
      undefined,
      { etag: onboarding.version, idempotencyKey: newIdempotencyKey() },
    )
  ).body;
}

export async function putBoard(
  boardId: string,
  input: BoardAutomationInput,
  version?: number,
): Promise<BoardAutomation> {
  return (
    await request<BoardAutomation>("PUT", `/api/v1/boards/${boardId}/automation`, input, {
      ...(version === undefined ? {} : { etag: version }),
      idempotencyKey: newIdempotencyKey(),
    })
  ).body;
}

export async function startGitHubConnection(
  boardId: string,
  forceNew = false,
): Promise<GitHubAppConnectionStart> {
  const query = forceNew ? "?force_new=true" : "";
  return (
    await request<GitHubAppConnectionStart>(
      "POST",
      `/api/v1/boards/${boardId}/automation/github-app/connection${query}`,
      {},
    )
  ).body;
}

export async function getGitHubConnection(
  boardId: string,
): Promise<GitHubAppConnection | null> {
  try {
    return (
      await request<GitHubAppConnection>(
        "GET",
        `/api/v1/boards/${boardId}/automation/github-app/connection`,
      )
    ).body;
  } catch (error) {
    if (error instanceof ApiProblem && error.status === 404) return null;
    throw error;
  }
}

export async function completeGitHubConnection(
  boardId: string,
  connection: GitHubAppConnection,
  input: GitHubAppConnectInput,
  version?: number,
): Promise<BoardAutomation> {
  return (
    await request<BoardAutomation>(
      "POST",
      `/api/v1/boards/${boardId}/automation/github-app/connections/${connection.id}/complete`,
      input,
      {
        ...(version === undefined ? {} : { etag: version }),
        idempotencyKey: newIdempotencyKey(),
      },
    )
  ).body;
}

export async function disconnectBoard(config: BoardAutomation): Promise<void> {
  await request<void>(
    "DELETE",
    `/api/v1/boards/${config.board_id}/automation`,
    undefined,
    { etag: config.version },
  );
}

export async function getWorkflow(
  boardId: string,
  ticketId: string,
): Promise<TicketWorkflow | null> {
  try {
    return (
      await request<TicketWorkflow>(
        "GET",
        `/api/v1/boards/${boardId}/tickets/${ticketId}/workflow`,
      )
    ).body;
  } catch (error) {
    if (error instanceof ApiProblem && error.status === 404) return null;
    throw error;
  }
}

export async function listWorkflows(
  boardId: string,
  ticketId: string,
): Promise<TicketWorkflowPage> {
  return (
    await request<TicketWorkflowPage>(
      "GET",
      `/api/v1/boards/${boardId}/tickets/${ticketId}/workflows`,
    )
  ).body;
}

export async function getWorkflowActivity(
  workflow: TicketWorkflow,
  query: WorkflowActivityQuery = {},
): Promise<TicketWorkflowActivity> {
  const params = new URLSearchParams();
  if (query.runId) params.set("run_id", query.runId);
  if (query.afterSequence !== undefined) {
    params.set("after_seq", String(query.afterSequence));
  }
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const suffix = params.size ? `?${params.toString()}` : "";
  return (
    await request<TicketWorkflowActivity>(
      "GET",
      `/api/v1/boards/${workflow.board_id}/tickets/${workflow.ticket_id}/workflows/${workflow.id}/activity${suffix}`,
    )
  ).body;
}

export async function yeet(
  boardId: string,
  ticketId: string,
  ticketVersion: number,
  input?: WorkflowYeetInput,
): Promise<TicketWorkflow> {
  return (
    await request<TicketWorkflow>(
      "POST",
      `/api/v1/boards/${boardId}/tickets/${ticketId}/workflow/yeet`,
      input ?? {},
      { etag: ticketVersion, idempotencyKey: newIdempotencyKey() },
    )
  ).body;
}

export async function approve(
  workflow: TicketWorkflow,
  note = "",
): Promise<TicketWorkflow> {
  return (
    await request<TicketWorkflow>(
      "POST",
      `/api/v1/boards/${workflow.board_id}/tickets/${workflow.ticket_id}/workflows/${workflow.id}/approve`,
      { note },
      { etag: workflow.version, idempotencyKey: newIdempotencyKey() },
    )
  ).body;
}

export async function iterate(
  workflow: TicketWorkflow,
  note: string,
): Promise<TicketWorkflow> {
  return (
    await request<TicketWorkflow>(
      "POST",
      `/api/v1/boards/${workflow.board_id}/tickets/${workflow.ticket_id}/workflows/${workflow.id}/iterate`,
      { note },
      { etag: workflow.version, idempotencyKey: newIdempotencyKey() },
    )
  ).body;
}

export async function cancel(workflow: TicketWorkflow): Promise<TicketWorkflow> {
  return (
    await request<TicketWorkflow>(
      "POST",
      `/api/v1/boards/${workflow.board_id}/tickets/${workflow.ticket_id}/workflows/${workflow.id}/cancel`,
      undefined,
      { etag: workflow.version, idempotencyKey: newIdempotencyKey() },
    )
  ).body;
}
